<template>
  <div data-test="duplicate-shipments" class="duplicate-shipments">
    <h1 class="mds-headline--medium">{{ $t("duplicate.duplicateBooking") }}</h1>
    <p class="mt-2">
      {{ $t("filter.bookedBy") }}
      <span class="mds-text--medium-bold">{{ getCompany() }}</span>
    </p>
  </div>

  <div class="mt-4" data-test="duplicate-shipments-container">
    <!-- Loading -->
    <mc-loading-indicator v-if="loading" data-test="shipments-loading" class="card card--loading" />

    <div v-else>
      <p class="mb-4">{{ $t("duplicate.description") }}</p>

      <!-- Filters -->
      <div data-test="duplicate-shipment-filters" class="duplicate-shipments__filters">
        <mc-select
          data-test="duplicate-shipment-origin"
          :label="$t('duplicate.origin') + ':'"
          :value="originOptionSelected || reducedDestinations[0].value"
          labelposition="left"
          @optionselected="handleOriginSelection"
        >
          <mc-option v-for="origin in reducedOrigins" :key="origin.value" :value="origin.value">
            {{ origin.label }}
          </mc-option>
        </mc-select>
        <mc-select
          data-test="duplicate-shipment-destination"
          :label="$t('duplicate.destination') + ':'"
          :value="destinationOptionSelected || reducedDestinations[0].value"
          labelposition="left"
          @optionselected="handleDestinationSelection"
        >
          <mc-option v-for="destination in reducedDestinations" :key="destination.value" :value="destination.value">
            {{ destination.label }}
          </mc-option>
        </mc-select>
        <mc-button
          data-test="duplicate-shipment-clear-button"
          appearance="neutral"
          variant="plain"
          :label="$t('filter.button.clear.label')"
          @click="clearFilters"
        />
      </div>

      <!-- Results -->
      <mc-table
        v-if="previousShipments && previousShipments.length > 0"
        class="duplicate-shipments__table"
        fit="small"
        sortdisabled
        :data.prop="previousShipments"
        :columns.prop="[
          {
            id: 'origin',
            label: $t('duplicate.origin'),
            verticalAlign: 'top'
          },
          {
            id: 'destination',
            label: $t('duplicate.destination'),
            verticalAlign: 'top'
          },
          {
            id: 'containers',
            label: $t('duplicate.containers'),
            verticalAlign: 'top'
          },
          {
            id: 'consignee',
            label: $t('filter.consignee'),
            verticalAlign: 'top'
          },
          {
            id: 'shipmentNumber',
            label: $t('duplicate.shipmentNumber'),
            verticalAlign: 'top'
          },
          {
            id: 'duplicate',
            verticalAlign: 'top'
          }
        ]"
        data-test="duplicate-table"
      >
        <template v-for="item in previousShipments" :key="item.id">
          <div :slot="`${item.id}_origin`">
            {{ item.originDisplayName }}
            <br />
            <span class="mds-neutral--weakest__text-color">
              {{ item.exportServiceModeName }} ({{ item.exportServiceModeCode }})
            </span>
          </div>
          <div :slot="`${item.id}_destination`">
            {{ item.destinationDisplayName }}
            <br />
            <span class="mds-neutral--weakest__text-color">
              {{ item.importServiceModeName }} ({{ item.importServiceModeCode }})
            </span>
          </div>
          <div :slot="`${item.id}_containers`">
            {{ item.containerQuantity }} x {{ $t(item.containerName) }}
            <br />
            <span
              class="mds-neutral--weakest__text-color duplicate-shipments__cargo-text"
              :title="item.containerCommodity?.length > 40 ? $t(item.containerCommodity) : ''"
              :class="item.containerCommodity?.length > 40 ? 'duplicate-shipments__cargo-text--hover' : ''"
            >
              {{ $t(item.containerCommodity) }}
            </span>
          </div>
          <div :slot="`${item.id}_consignee`">
            {{ item.consignee || "-" }}
          </div>
          <div :slot="`${item.id}_shipmentNumber`">
            <a
              :href="`/shipment-details/${item.shipmentNumber}`"
              data-test="document-table-shipment-link"
              class="mds-text--small-bold color-primary"
              @click="trackShipmentClick('duplicateshipments', $route?.path?.toLowerCase()?.includes?.('export'))"
            >
              {{ item.shipmentNumber }}
            </a>
          </div>
          <div :slot="`${item.id}_duplicate`">
            <a
              :href="`/book/?bookingNumber=${item.shipmentNumber}&bookingType=duplicate`"
              class="color-primary"
              data-test="document-table-duplicate-link"
              @click="trackDuplicateBookingClick(item)"
            >
              <span class="icon-align-left">
                <mc-icon icon="file-copy" size="20" />
              </span>
              <b>{{ $t("duplicate.duplicate") }}</b></a
            >
          </div>
        </template>
      </mc-table>

      <!-- No results -->
      <div v-else class="mt-2" data-test="duplicate-no-results">
        {{ $t("duplicate.noBookings") }}
      </div>
    </div>
  </div>
</template>
<script>
import { mapActions, mapState } from "pinia";
import { duplicateShipments } from "@/store/duplicateShipments";
import { getCompany, trackShipmentClick } from "@/lib/utilities";
import "@maersk-global/mds-components-core/mc-table";

export default {
  name: "DuplicateShipments",
  data() {
    return {
      originOptionSelected: "all_origins",
      destinationOptionSelected: "all_destinations",
      selectedRow: null
    };
  },
  computed: {
    ...mapState(duplicateShipments, [
      "allOrigins",
      "allDestinations",
      "reducedDestinations",
      "reducedOrigins",
      "loading",
      "error",
      "previousShipments"
    ])
  },
  watch: {
    originOptionSelected: {
      handler: function () {
        this.updateReducedDestinations(this.originOptionSelected);
        this.updatePreviousShipments(this.originOptionSelected, this.destinationOptionSelected);
      }
    },
    destinationOptionSelected: {
      handler: function () {
        this.updateReducedOrigins(this.destinationOptionSelected);
        this.updatePreviousShipments(this.originOptionSelected, this.destinationOptionSelected);
      }
    },
    reducedDestinations: {
      handler: function () {
        if (this.reducedDestinations.length === 1) {
          this.destinationOptionSelected = this.reducedDestinations[0].value;
        }
      },
      deep: true
    },
    reducedOrigins: {
      handler: function () {
        if (this.reducedOrigins.length === 1) {
          this.originOptionSelected = this.reducedOrigins[0].value;
        }
      },
      deep: true
    }
  },
  async mounted() {
    await this.getPreviousShipments();
    await this.updateOriginsAndDestinations();
  },
  methods: {
    ...mapActions(duplicateShipments, [
      "getPreviousShipments",
      "updatePreviousShipments",
      "updateOriginsAndDestinations",
      "updateReducedDestinations",
      "updateReducedOrigins"
    ]),
    trackShipmentClick,
    getCompany,
    handleOriginSelection($event) {
      this.originOptionSelected = $event.target.value;
      this.$sendGAEvent({
        event: "UXEvent",
        action: "Origin Destination QF",
        category: "Duplicate booking",
        label: "origin"
      });
    },
    handleDestinationSelection($event) {
      this.destinationOptionSelected = $event.target.value;
      this.$sendGAEvent({
        event: "UXEvent",
        action: "Origin Destination QF",
        category: "Duplicate booking",
        label: "destination"
      });
    },
    clearFilters() {
      this.originOptionSelected = "all_origins";
      this.destinationOptionSelected = "all_destinations";
      this.updateOriginsAndDestinations();
    },
    showDetails(index) {
      if (this.selectedRow === index) {
        this.selectedRow = null;
      } else {
        this.selectedRow = index;
      }
    },
    trackDuplicateBookingClick(shipment) {
      this.$sendGAEvent({
        event: "DQCS",
        category: "SO_Duplicate",
        action: "Click",
        label: {
          data: "OutboundClickToDuplicate",
          originCountry: shipment.originCountryCode,
          destinationCountry: shipment.destinationCountryCode
        }
      });
    }
  }
};
</script>
